import { useEffect, useState } from 'react';
import styled from 'styled-components';

// Components
import FontAwesomeIcon from '../../components/common/FontAwesomeIcon';
import SimpleLoading from '../../components/common/SimpleLoading';
import CommonButton from '../../components/common/CommonButton';

// Libraries
import { useParams } from 'react-router-dom';
import html2canvas from 'html2canvas';
import parse from 'html-react-parser';
import { jsPDF } from 'jspdf';

// API
import { getAchievement } from './api/getAchievement';
import SharedAchivement from './SharedAchivement';
import { useQueryParams } from '../../hooks/useQueryParams';
import { useSelector } from 'react-redux';

const Achievement = () => {
    const { repo_id, id } = useParams();
    const params = useQueryParams();
    const { user } = useSelector((state) => state.auth);

    const certificateOnly = Boolean(params.get('certificate_only'));

    const [achievementHTML, setAchievementHTML] = useState(null);
    const [openShared, setOpenShared] = useState(false);

    // Función para recortar espacio en blanco del canvas
    const trimCanvas = (canvas) => {
        const ctx = canvas.getContext('2d');
        const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = pixels.data;
        let top = canvas.height;
        let bottom = 0;
        let left = canvas.width;
        let right = 0;

        // Buscar límites del contenido (píxeles no blancos)
        for (let y = 0; y < canvas.height; y++) {
            for (let x = 0; x < canvas.width; x++) {
                const i = (y * canvas.width + x) * 4;
                const alpha = data[i + 3];
                // Si el píxel no es transparente y no es blanco puro
                if (
                    alpha > 0 &&
                    (data[i] < 250 || data[i + 1] < 250 || data[i + 2] < 250)
                ) {
                    if (y < top) top = y;
                    if (y > bottom) bottom = y;
                    if (x < left) left = x;
                    if (x > right) right = x;
                }
            }
        }

        // Agregar un margen pequeño (2%)
        const marginX = Math.floor((right - left) * 0.02);
        const marginY = Math.floor((bottom - top) * 0.02);

        top = Math.max(0, top - marginY);
        bottom = Math.min(canvas.height, bottom + marginY);
        left = Math.max(0, left - marginX);
        right = Math.min(canvas.width, right + marginX);

        const width = right - left;
        const height = bottom - top;

        // Crear nuevo canvas con el contenido recortado
        const trimmed = document.createElement('canvas');
        trimmed.width = width;
        trimmed.height = height;
        const trimmedCtx = trimmed.getContext('2d');
        trimmedCtx.drawImage(
            canvas,
            left,
            top,
            width,
            height,
            0,
            0,
            width,
            height
        );

        return trimmed;
    };

    const fetchAchievement = async () => {
        // Llamada a la API para obtener el certificado
        const response = await getAchievement(repo_id, id);
        setAchievementHTML(response);
    };

    useEffect(() => {
        fetchAchievement();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const comportamientoInfo = {
        partner_id: user?.partner_id,
        partner_name: user?.name,
    };

    // Función auxiliar para preparar el elemento para captura
    const prepareElementForCapture = (element) => {
        // Obtener estilos computados para capturar el estado real
        const computedStyle = window.getComputedStyle(element);

        // Guardar todos los estilos originales
        const originalStyles = {
            width: element.style.width || '',
            height: element.style.height || '',
            position: element.style.position || '',
            left: element.style.left || '',
            top: element.style.top || '',
            transform: element.style.transform || '',
            transformOrigin: element.style.transformOrigin || '',
            padding: element.style.padding || '',
            margin: element.style.margin || '',
            maxWidth: element.style.maxWidth || '',
        };

        // También necesitamos obtener el contenedor padre para remover sus transformaciones
        const parentContainer = element.parentElement;
        const parentOriginalStyles = {
            transform: parentContainer?.style.transform || '',
            transformOrigin: parentContainer?.style.transformOrigin || '',
        };

        // Remover transformaciones del elemento y establecer dimensiones fijas
        element.style.width = '1754px';
        element.style.height = 'auto';
        element.style.position = 'absolute';
        element.style.left = '-9999px';
        element.style.top = '0px';
        element.style.transform = 'none';
        element.style.transformOrigin = 'top left';
        element.style.padding = '0';
        element.style.margin = '0';
        element.style.maxWidth = 'none';

        // Remover transformaciones del contenedor padre si existe
        if (parentContainer) {
            parentContainer.style.transform = 'none';
            parentContainer.style.transformOrigin = 'top left';
        }

        return { originalStyles, parentOriginalStyles, parentContainer };
    };

    // Función auxiliar para restaurar estilos originales
    const restoreElementStyles = (
        element,
        originalStyles,
        parentContainer,
        parentOriginalStyles
    ) => {
        element.style.width = originalStyles.width;
        element.style.height = originalStyles.height;
        element.style.position = originalStyles.position;
        element.style.left = originalStyles.left;
        element.style.top = originalStyles.top;
        element.style.transform = originalStyles.transform;
        element.style.transformOrigin = originalStyles.transformOrigin;
        element.style.padding = originalStyles.padding;
        element.style.margin = originalStyles.margin;
        element.style.maxWidth = originalStyles.maxWidth;

        if (parentContainer && parentOriginalStyles) {
            parentContainer.style.transform = parentOriginalStyles.transform;
            parentContainer.style.transformOrigin =
                parentOriginalStyles.transformOrigin;
        }
    };

    const generatePDF = async () => {
        // Enviar a la API de comportamiento
        fetch('https://behaviourapi.aden.org/mvp_log/log', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'api-key': '4ba4dc27-f800-44f3-8576-bbb42826d8f4',
            },
            body: JSON.stringify({
                site: window.location.href,
                action: 'insignia_descargar_copiar_link',
                detail: JSON.stringify(comportamientoInfo),
                email: window.personal_email,
            }),
        }).then((response) => response.json());

        const element = document.getElementById('toPdf');
        if (!element) {
            console.error('Element #toPdf not found');
            return;
        }

        const { originalStyles, parentOriginalStyles, parentContainer } =
            prepareElementForCapture(element);

        await new Promise((resolve) => setTimeout(resolve, 300));

        html2canvas(element, {
            useCORS: true,
            allowTaint: true,
            scale: 2,
            width: element.scrollWidth,
            height: element.scrollHeight,
            windowWidth: element.scrollWidth,
            windowHeight: element.scrollHeight,
            backgroundColor: '#ffffff',
            logging: false,
        })
            .then((canvas) => {
                restoreElementStyles(
                    element,
                    originalStyles,
                    parentContainer,
                    parentOriginalStyles
                );

                const trimmedCanvas = trimCanvas(canvas);

                const imgData = trimmedCanvas.toDataURL('image/PNG');
                const pdf = new jsPDF('l', 'mm', 'a4'); // A4 horizontal

                const pdfWidth = 297; // Ancho A4 en mm
                const pdfHeight = 210; // Alto A4 en mm
                const imgAspectRatio =
                    trimmedCanvas.width / trimmedCanvas.height;
                const pdfAspectRatio = pdfWidth / pdfHeight;

                let finalWidth, finalHeight, xOffset, yOffset;

                // Ajustar imagen para que quepa en el PDF manteniendo proporción
                if (imgAspectRatio > pdfAspectRatio) {
                    finalWidth = pdfWidth - 20; // Margen de 10mm a cada lado
                    finalHeight = finalWidth / imgAspectRatio;
                    xOffset = 10;
                    yOffset = (pdfHeight - finalHeight) / 2;
                } else {
                    finalHeight = pdfHeight - 20; // Margen de 10mm arriba y abajo
                    finalWidth = finalHeight * imgAspectRatio;
                    xOffset = (pdfWidth - finalWidth) / 2;
                    yOffset = 10;
                }

                pdf.addImage(
                    imgData,
                    'PNG',
                    xOffset,
                    yOffset,
                    finalWidth,
                    finalHeight,
                    undefined,
                    'FAST'
                );
                pdf.save('certificado.pdf');
            })
            .catch((error) => {
                restoreElementStyles(
                    element,
                    originalStyles,
                    parentContainer,
                    parentOriginalStyles
                );
                console.error('Error generating PDF:', error);
            });
    };

    const handleDownloadImage = async () => {
        // Enviar a la API de comportamiento
        fetch('https://behaviourapi.aden.org/mvp_log/log', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'api-key': '4ba4dc27-f800-44f3-8576-bbb42826d8f4',
            },
            body: JSON.stringify({
                site: window.location.href,
                action: 'insignia_descargar_copiar_link',
                detail: JSON.stringify(comportamientoInfo),
                email: window.personal_email,
            }),
        }).then((response) => response.json());

        const element = document.getElementById('toPdf');
        if (!element) {
            console.error('Element #toPdf not found');
            return;
        }

        const { originalStyles, parentOriginalStyles, parentContainer } =
            prepareElementForCapture(element);

        await new Promise((resolve) => setTimeout(resolve, 300));

        html2canvas(element, {
            useCORS: true,
            scale: 2,
            width: element.scrollWidth,
            height: element.scrollHeight,
            windowWidth: element.scrollWidth,
            windowHeight: element.scrollHeight,
            backgroundColor: '#ffffff',
            logging: false,
        })
            .then((canvas) => {
                restoreElementStyles(
                    element,
                    originalStyles,
                    parentContainer,
                    parentOriginalStyles
                );

                const trimmedCanvas = trimCanvas(canvas);

                const link = document.createElement('a');
                link.href = trimmedCanvas.toDataURL('image/png');
                link.download = 'certificado.png';
                link.click();
            })
            .catch((error) => {
                restoreElementStyles(
                    element,
                    originalStyles,
                    parentContainer,
                    parentOriginalStyles
                );
                console.error('Error generating image:', error);
            });
    };

    const handleOpenShareModal = () => {
        // Enviar a la API de comportamiento
        fetch('https://behaviourapi.aden.org/mvp_log/log', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'api-key': '4ba4dc27-f800-44f3-8576-bbb42826d8f4',
            },
            body: JSON.stringify({
                site: window.location.href,
                action: 'insignia_descargar_copiar_link',
                detail: JSON.stringify(comportamientoInfo),
                email: window.personal_email,
            }),
        }).then((response) => response.json());
        setOpenShared(true);
    };

    return (
        <Container>
            <SharedAchivement
                modalIsOpen={openShared}
                handleCloseModal={() => setOpenShared(false)}
                link={`${window.location.origin}/logros/${repo_id}/${id}`}
            />
            {!certificateOnly && (
                <Aside>
                    <h2>{achievementHTML?.title}</h2>
                    <p>{achievementHTML?.description}</p>
                    <p>
                        ID de la credencial: <b>{achievementHTML?.id}</b>
                    </p>
                    <h4>OPCIONES</h4>
                    <CommonButton
                        onClick={generatePDF}
                        label="Descargar como PDF"
                        variant="filled"
                        Icon={() => (
                            <div>
                                <FontAwesomeIcon
                                    icon="fa-solid fa-file-pdf"
                                    color="#fff"
                                    size={16}
                                />
                            </div>
                        )}
                        data-behaviour-action={'insignia_descargar_copiar_link'}
                        data-behaviour-detail={JSON.stringify({
                            insignia_id: achievementHTML?.id,
                            insignia_nombre: achievementHTML?.title,
                            ...comportamientoInfo,
                        })}
                    />
                    <CommonButton
                        onClick={handleDownloadImage}
                        label="Descargar como Imagen"
                        variant="filled"
                        Icon={() => (
                            <div>
                                <FontAwesomeIcon
                                    icon="fa-solid fa-image"
                                    color="#fff"
                                    size={16}
                                />
                            </div>
                        )}
                        data-behaviour-action={'insignia_descargar_copiar_link'}
                        data-behaviour-detail={JSON.stringify({
                            insignia_id: achievementHTML?.id,
                            insignia_nombre: achievementHTML?.title,
                            ...comportamientoInfo,
                        })}
                    />
                    <CommonButton
                        onClick={handleOpenShareModal}
                        label="Compartir"
                        variant="filled"
                        Icon={() => (
                            <div>
                                <FontAwesomeIcon
                                    icon="fa-solid fa-link"
                                    color="#fff"
                                    size={16}
                                />
                            </div>
                        )}
                        data-behaviour-action={'insignia_descargar_copiar_link'}
                        data-behaviour-detail={JSON.stringify({
                            insignia_id: achievementHTML?.id,
                            insignia_nombre: achievementHTML?.title,
                            ...comportamientoInfo,
                        })}
                    />

                    <section>
                        <div>
                            <h5>EXPEDIDO EL</h5>
                            <p>{achievementHTML?.generation_date}</p>
                        </div>
                        <div>
                            <h5>EXPIRA EL</h5>
                            <p
                                style={{
                                    color: achievementHTML?.expiration_date
                                        ? 'black'
                                        : '#A8A8A8',
                                }}
                            >
                                {achievementHTML?.expiration_date ||
                                    'No caduca'}
                            </p>
                        </div>
                    </section>
                </Aside>
            )}
            {!achievementHTML ? (
                <CertificateContainer>
                    {!certificateOnly && <SimpleLoading />}
                </CertificateContainer>
            ) : (
                <CertificateContainer>
                    <div id="toPdf">{parse(achievementHTML?.html)}</div>
                </CertificateContainer>
            )}
        </Container>
    );
};

export default Achievement;

const Container = styled.div`
    box-sizing: border-box;
    width: 100%;
    min-height: 100vh;
    height: 100%;
    display: flex;
    justify-content: space-between;
    overflow-y: auto;
    overflow-x: hidden;

    #toPdf {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 70vw;
        max-width: 100%;

        .certificadocontainer {
            img:first-of-type {
                margin-bottom: -2rem;
            }
            img:nth-of-type(2) {
                margin-top: -10rem;
            }
            img:nth-of-type(4) {
                margin-top: -6rem;
            }
        }
    }

    /* Tablet y pantallas medianas */
    @media screen and (max-width: 1200px) {
        #toPdf {
            width: 65vw;
        }
    }

    /* Móvil y pantallas pequeñas */
    @media screen and (max-width: 768px) {
        flex-direction: column;
        overflow-y: auto;
        height: auto;
        min-height: 100vh;

        #toPdf {
            width: 100%;
            padding: 1rem;
            order: 2;
        }

        #toPdf:has(.certificadocontainer) {
            padding: 0.5rem;
        }

        #toPdf:has(.main) {
            width: 100%;
        }
    }
`;

const Aside = styled.span`
    box-sizing: border-box;
    width: calc(30vw - 4rem);
    min-width: 250px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 2rem;

    section {
        display: flex;
        gap: 2rem;
        margin-top: 1rem;
        div {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
        }
    }

    /* Tablet */
    @media screen and (max-width: 1200px) {
        width: calc(35vw - 3rem);
        padding: 1.5rem;
    }

    /* Móvil */
    @media screen and (max-width: 768px) {
        width: 100%;
        min-width: unset;
        padding: 1.5rem;
        order: 1;
        gap: 0.75rem;
    }
`;

const CertificateContainer = styled.section`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 2rem;
    overflow: hidden;
    #toPdf {
        transform: scale(0.9);
        transform-origin: top center;
    }

    /* Tablet */
    @media screen and (max-width: 1200px) {
        #toPdf {
            transform: scale(0.7);
            transform-origin: top center;
        }
    }

    /* Móvil - Vista normal sin escalado excesivo */
    @media screen and (max-width: 768px) {
        padding: 1rem;
        width: 100%;
        height: 60vh;

        #toPdf {
            width: 100%;
            transform: scale(0.5);
            transform-origin: center;
        }
    }

    /* Móvil pequeño */
    @media screen and (max-width: 425px) {
        padding: 0.5rem;
        width: 100%;

        #toPdf {
            transform: scale(0.25);
            transform-origin: center;
        }

        #toPdf:has(.certificadocontainer) {
            transform: scale(0.5);
        }

        #toPdf:has(.main) {
            transform: scale(0.6);
        }
    }
`;
